import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '../../lib/utils';
import { useResponsive } from '../../shared/hooks/useResponsive';
import { ThreadUnreadBadge } from '../../shared/components/common/ThreadUnreadBadge';

const Tabs = TabsPrimitive.Root;

/**
 * TabsList variants:
 * - "default": Compact pill-style tabs with muted background (for settings, dialogs)
 * - "underline": Full-width tabs with bottom border (for detail pages - more prominent)
 */
type TabsListVariant = 'default' | 'underline';

interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> {
  variant?: TabsListVariant;
}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant = 'default', ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      variant === 'underline'
        ? 'inline-flex w-full items-center justify-start border-b bg-transparent h-auto p-0 text-muted-foreground overflow-x-auto scrollbar-hidden'
        // max-w-full caps the content-driven inline-flex width so overflow-x-auto can engage at 375px.
        : 'inline-flex h-10 max-w-full items-center justify-center rounded-md bg-muted p-1 text-muted-foreground overflow-x-auto scrollbar-hidden',
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

/**
 * A `TabsList` that stays usable when its triggers are wider than the available
 * space — the standard for entity detail pages, whose related-entity tab strips
 * grow past the card at narrow widths.
 *
 * The list itself is the horizontal scroller (`overflow-x-auto`, native swipe on
 * touch, roving-tabindex arrow keys reach every tab and scroll it into view). On
 * pointer devices, where the scrollbar is hidden and there is no swipe, it adds
 * two affordances so the hidden tabs are reachable and discoverable:
 *  - Chevron buttons at the start/end edge, each shown only while there is more
 *    to scroll that way. Clicking pages the strip by ~75% of its width.
 *  - Shift + mouse-wheel scrolls horizontally (mirrors `KanbanScrollContainer`).
 *
 * Drop-in for `TabsList`: same props, and the forwarded ref still points at the
 * underlying list. Do NOT wrap this in a `ScrollArea` or override its width — it
 * owns both.
 */
const ScrollableTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant = 'default', children, ...props }, forwardedRef) => {
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const { isTouchDevice } = useResponsive();
  const [edges, setEdges] = React.useState({ start: false, end: false });

  const measure = React.useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    // `scrollLeft` is negative in RTL on modern browsers; abs() gives the
    // distance travelled from the start edge in both directions.
    const scrolled = Math.abs(el.scrollLeft);
    const maxScroll = el.scrollWidth - el.clientWidth;
    const start = scrolled > 1;
    const end = scrolled < maxScroll - 1;
    setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
  }, []);

  // Re-measure when the trigger set changes (tabs added/removed shift scrollWidth).
  React.useLayoutEffect(() => {
    measure();
  }, [measure, children]);

  React.useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => measure();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [measure]);

  // Non-passive wheel listener so Shift+Wheel can be redirected to horizontal
  // scroll (desktop mice without a horizontal wheel). React's synthetic wheel
  // events are passive and cannot preventDefault.
  React.useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.shiftKey) return;
      const delta = e.deltaY !== 0 ? e.deltaY : e.deltaX;
      if (delta === 0) return;
      e.preventDefault();
      el.scrollLeft += delta;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const page = React.useCallback((dir: 'start' | 'end') => {
    const el = listRef.current;
    if (!el) return;
    const rtl = typeof window !== 'undefined' && getComputedStyle(el).direction === 'rtl';
    const amount = Math.max(el.clientWidth * 0.75, 120);
    // Toward the end edge increases the visual offset; RTL flips the sign.
    const signed = (dir === 'end' ? amount : -amount) * (rtl ? -1 : 1);
    el.scrollBy({ left: signed, behavior: 'smooth' });
  }, []);

  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      listRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [forwardedRef],
  );

  // Native swipe covers touch; the chevrons are a pointer-only affordance and are
  // redundant with the tablist's arrow-key navigation, so they stay out of the
  // tab order and the a11y tree.
  const showButtons = !isTouchDevice;

  return (
    <div className="relative">
      <TabsList ref={setRefs} variant={variant} className={className} {...props}>
        {children}
      </TabsList>
      {showButtons && edges.start && (
        <TabScrollAffordance direction="start" onClick={() => page('start')} />
      )}
      {showButtons && edges.end && (
        <TabScrollAffordance direction="end" onClick={() => page('end')} />
      )}
    </div>
  );
});
ScrollableTabsList.displayName = 'ScrollableTabsList';

/**
 * One edge's scroll affordance: a `bg-card`→transparent fade scrim under a
 * chevron button. The scrim makes the tab clipped at that edge read as "there's
 * more" rather than sharply cut off, and — because the whole affordance is
 * `pointer-events-none` except the small button — clicks over the faded area
 * still fall through to the tab underneath; only the button itself scrolls.
 * Physical gradient directions are mirrored with the `rtl:` variant so the fade
 * stays anchored to the logical edge.
 */
function TabScrollAffordance({
  direction,
  onClick,
}: {
  direction: 'start' | 'end';
  onClick: () => void;
}) {
  const isStart = direction === 'start';
  const Icon = isStart ? ChevronLeft : ChevronRight;
  return (
    <div
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-y-0 z-10 flex items-center',
        isStart ? 'start-0 justify-start' : 'end-0 justify-end',
      )}
    >
      {/* Fade scrim — opaque at the edge, transparent toward the centre. */}
      <div
        className={cn(
          'absolute inset-y-0 w-12 from-card',
          isStart
            ? 'start-0 bg-gradient-to-r rtl:bg-gradient-to-l'
            : 'end-0 bg-gradient-to-l rtl:bg-gradient-to-r',
        )}
      />
      <button
        type="button"
        tabIndex={-1}
        data-testid={`tab-scroll-${direction}`}
        onClick={onClick}
        className={cn(
          'pointer-events-auto relative flex h-7 w-7 items-center justify-center',
          'rounded-full border border-border bg-card text-muted-foreground shadow-sm',
          'transition-colors hover:bg-accent hover:text-foreground',
        )}
      >
        <Icon className="h-4 w-4 rtl:rotate-180" />
      </button>
    </div>
  );
}

/**
 * TabsTrigger variants (should match TabsList variant):
 * - "default": Rounded with background on active
 * - "underline": Bottom border indicator on active
 */
type TabsTriggerVariant = 'default' | 'underline';

interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  variant?: TabsTriggerVariant;
  /** Optional icon to display before the tab label */
  icon?: React.ComponentType<{ className?: string }>;
}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant = 'default', icon: Icon, children, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      variant === 'underline'
        ? 'inline-flex items-center justify-center whitespace-nowrap px-2.5 sm:px-4 py-3 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-b-2 border-transparent data-[state=active]:border-module-accent data-[state=active]:text-foreground flex-shrink-0'
        // Pill (default) active state: carry the module hue as a low-alpha
        // accent WASH (the same selection treatment as the nav selected item),
        // keeping the label as `text-foreground` ink so it stays AA-legible on
        // every module hue — the accent itself is only a 3:1 non-text mark, not a
        // 4.5:1 text color. `shadow-sm` keeps the pill's lift off the muted track.
        // Achromatic modules fall back to the slate accent default. Underline
        // variant above is unchanged.
        : 'inline-flex flex-shrink-0 items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-module-accent/15 dark:data-[state=active]:bg-module-accent/25 data-[state=active]:text-foreground data-[state=active]:shadow-sm',
      className
    )}
    {...props}
  >
    {Icon && <Icon className="h-4 w-4 me-2" />}
    {children}
  </TabsPrimitive.Trigger>
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

interface TabsTriggerWithIndicatorProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  /** Boolean dot indicator (legacy "has content" cue). */
  hasContent?: boolean;
  variant?: TabsTriggerVariant;
  /** Optional icon to display before the tab label */
  icon?: React.ComponentType<{ className?: string }>;
  /**
   * Total messages in the entity's thread. When defined, renders the read/unread
   * `ThreadUnreadBadge` inline (trailing) instead of the boolean `hasContent`
   * dot — nothing when `0`, a muted dot when all read, an accent count when
   * `unreadCount > 0`. Pair with `useThreadMarkRead` + `useThreadLiveRefresh`.
   */
  messageCount?: number;
  /** Unread messages for the current user (used with `messageCount`). */
  unreadCount?: number;
}

const TabsTriggerWithIndicator = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerWithIndicatorProps
>(({ className, hasContent, variant = 'default', icon: Icon, messageCount, unreadCount, children, ...props }, ref) => {
  const hasThreadCounts = messageCount !== undefined;
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'relative',
        variant === 'underline'
          ? 'inline-flex items-center justify-center whitespace-nowrap px-2.5 sm:px-4 py-3 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-b-2 border-transparent data-[state=active]:border-module-accent data-[state=active]:text-foreground flex-shrink-0'
          // Pill active state carries the module hue as an accent wash with an
          // ink label — see TabsTrigger above.
          : 'inline-flex flex-shrink-0 items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-module-accent/15 dark:data-[state=active]:bg-module-accent/25 data-[state=active]:text-foreground data-[state=active]:shadow-sm',
        className
      )}
      {...props}
    >
      {Icon && <Icon className="h-4 w-4 me-2" />}
      {children}
      {hasThreadCounts ? (
        <ThreadUnreadBadge
          messageCount={messageCount}
          unreadCount={unreadCount ?? 0}
          className="ms-1.5"
        />
      ) : (
        hasContent && (
          <span className="absolute -top-0.5 -end-0.5 h-2 w-2 rounded-full bg-primary" />
        )
      )}
    </TabsPrimitive.Trigger>
  );
});
TabsTriggerWithIndicator.displayName = 'TabsTriggerWithIndicator';

export { Tabs, TabsList, ScrollableTabsList, TabsTrigger, TabsContent, TabsTriggerWithIndicator };
